import * as React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "../Components/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../Components/Card";
import FadeIn from "../Animations/FadeIn";
import { GetCourseById } from "../DataService/CourseService";
import { useUser } from "../DataService/UserContext";
import { CartItem, CheckoutContactInfo, CheckoutState } from "../Types/CartTypes";
import { Course } from "../Types/CourseTypes";

const emptyContact: CheckoutContactInfo = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  postalCode: "",
};

export const RotBalancePayment = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [course, setCourse] = React.useState<Course | null>(null);
  const prefill = (location.state as { firstName?: string; lastName?: string; email?: string; paymentStage?: string } | null) ?? null;
  const paymentStage = (prefill?.paymentStage ?? new URLSearchParams(location.search).get("stage") ?? "balance").toLowerCase();
  const isFinalBalance = paymentStage === "balance";
  const [contact, setContact] = React.useState<CheckoutContactInfo>({
    ...emptyContact,
    firstName: prefill?.firstName ?? "",
    lastName: prefill?.lastName ?? "",
    email: prefill?.email ?? user?.email ?? "",
  });

  React.useEffect(() => {
    setContact(prev => prev.email ? prev : { ...prev, email: prefill?.email ?? user?.email ?? "" });
  }, [prefill?.email, user?.email]);

  React.useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        if (!courseId) {
          setError("Saknar kurs-ID för betalningen.");
          return;
        }

        const loadedCourse = await GetCourseById(Number(courseId));
        if (!mounted) return;

        if (!loadedCourse) {
          setError("Kunde inte hämta ROT-programmet.");
          return;
        }

        setCourse(loadedCourse);
      } catch {
        if (!mounted) return;
        setError("Kunde inte hämta ROT-programmet.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [courseId]);

  const handleChange = (field: keyof CheckoutContactInfo, value: string) => {
    setContact(prev => ({ ...prev, [field]: value }));
  };

  const handleContinue = () => {
    if (!course) return;

    const registrationFee = Number(course.registrationFee ?? 0);
    const fullPrice = Number(course.price ?? 0);
    const balanceAmount = Math.max(fullPrice - registrationFee, 0);
    const amount = isFinalBalance ? balanceAmount : registrationFee;

    if (isFinalBalance && balanceAmount <= 0) {
      setError("Slutbetalningen är inte satt för detta program.");
      return;
    }

    if (!isFinalBalance && registrationFee <= 0) {
      setError("Registreringsavgiften är inte satt för detta program.");
      return;
    }

    const item: CartItem = {
      id: `rot-${isFinalBalance ? "balance" : "registration"}-${course.id}`,
      type: "program",
      referenceId: String(course.id),
      title: isFinalBalance ? `ROT-slutbetalning: ${course.title}` : `ROT-registrering: ${course.title}`,
      description: isFinalBalance ? "Återstående betalning för ROT-programmet" : "Registreringsavgift för ROT-programmet",
      price: amount,
      quantity: 1,
      courseId: course.id,
      metadata: {
        programKey: "rot",
        programStartDate: course.startDate ?? "",
        paymentStage: isFinalBalance ? "balance" : "registration",
        registrationFeePaid: !isFinalBalance,
        finalPaymentDueAt: course.finalPaymentDeadline ?? "",
      },
    };

    const state: CheckoutState = {
      items: [item],
      contact,
      totalAmount: amount,
    };

    navigate("/payment", { state });
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 24px" }}>
      <FadeIn>
        <p style={{ color: "#9f5255", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>ROT-programmet</p>
        <h1 style={{ fontSize: 42, margin: "8px 0 12px" }}>{isFinalBalance ? "Betala slutbetalningen" : "Betala registreringsavgiften"}</h1>
        <p style={{ color: "#6b7280", maxWidth: 760 }}>
          {isFinalBalance
            ? "Här kan du slutföra betalningen för ROT-programmet när registreringen redan är klar."
            : "Här betalar du registreringsavgiften för ROT-programmet efter att du blivit antagen från väntelistan."}
        </p>
      </FadeIn>

      {loading && <p style={{ marginTop: 24 }}>Laddar ROT-programmet...</p>}
      {error && <p style={{ marginTop: 24, color: "#b91c1c" }}>{error}</p>}

      {!loading && !error && course && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24, marginTop: 32 }}>
          <Card>
            <CardHeader>
              <CardTitle>{course.title}</CardTitle>
            </CardHeader>
            <CardContent style={{ display: "grid", gap: 12 }}>
              <p>{course.description}</p>
              <div style={{ display: "grid", gap: 8, color: "#6b7280" }}>
                <span>Startdatum: {course.startDate ? new Date(course.startDate).toLocaleDateString("sv-SE") : "Ej satt"}</span>
                <span>Slutbetalning senast: {course.finalPaymentDeadline ? new Date(course.finalPaymentDeadline).toLocaleDateString("sv-SE") : "Ej satt"}</span>
                <span>Registreringsavgift: {course.registrationFee ?? 0} kr</span>
                <span>Fullpris: {course.price ?? 0} kr</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Dina uppgifter</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {([ ["firstName", "Förnamn"], ["lastName", "Efternamn"], ["email", "E-post"], ["phone", "Telefon"], ["address", "Adress"], ["city", "Stad"], ["postalCode", "Postnummer"] ] as const).map(([field, label]) => (
                  <label key={field} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span>{label}</span>
                    <input
                      value={contact[field]}
                      onChange={(e) => handleChange(field, e.target.value)}
                      placeholder={label}
                      style={{ padding: "12px 14px", border: "1px solid #d1d5db", borderRadius: 8 }}
                    />
                  </label>
                ))}
              </div>

              <div style={{ marginTop: 20, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Button text="Tillbaka" variant="secondary" onClick={() => navigate(-1)} />
                <Button
                  text={isFinalBalance
                    ? `Fortsätt till slutbetalning (${Math.max(Number(course.price ?? 0) - Number(course.registrationFee ?? 0), 0)} kr)`
                    : `Fortsätt till registreringsavgiften (${Number(course.registrationFee ?? 0)} kr)`}
                  onClick={handleContinue}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RotBalancePayment;
